import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { FragmentApiProvider, useFragmentApi } from '../Services/FragmentApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function FragmentHub({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getFragments, deleteFragment } = useFragmentApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fragments, setFragments] = useState([]);
  const [totalFragments, setTotalFragments] = useState(0);

  const tableTitle = 'Fragments';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/fragments/${row.key}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete this Fragment?`, async () => {
      try {
        setLoading(true);
        await deleteFragment(selectedId);
        await fetchFragments(storedSkip(), storedLimit());
        showAlert('success', 'Fragment deleted!');
      } catch (err) {
        console.error(err);
        showAlert('fail', 'Failed to delete Fragment!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchFragments = async (skip, limit) => {
    setLoading(true);
    try {
      const response = await getFragments(skip, limit);
      console.log('Fetched Fragments:', response);
      setFragments(response.items);
      setTotalFragments(response.totalCount);
      setError(null);
    } catch (err) {
      console.error('Error fetching Fragments:', err);
      setError('Failed to load Fragments');
      setFragments([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchFragments(storedSkip(), storedLimit());
  }, []);

  const columns = [
    { name: 'ID', selector: (row) => row.key },
    { name: 'Name', selector: (row) => row.name },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive') },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at) },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at) },
  ];

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={fragments}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalFragments}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchFragments}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/fragments/new-fragment`)
                  }
                >
                  Create New Fragment
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FragmentHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <FragmentApiProvider>
        <FragmentHub canEdit={canEdit} />
      </FragmentApiProvider>
    </ConfirmationProvider>
  );
}

export default FragmentHubWrapper;
